import { EFFETS_DU_SOCLE, estEffetDeFiche, PREFIXE_EFFET_SOCLE } from '@taki/data-model';
import type { EffetDeFiche, EffetDuSocle } from '@taki/data-model';
import type { ResultatLancement, TakiSdk } from '@taki/sdk';
import type { ContexteDAction } from '@taki/ui';

/**
 * LES EFFETS TENUS PAR LE SOCLE : ce qu'une action nomme `socle:<nom>` fait au
 * clic, ecrit ICI une fois pour toutes les apps et toutes les orgs.
 *
 * POURQUOI ILS EXISTENT. L'effet d'une action vit dans le CODE de l'app
 * (`effets` de `definirAppDeclaree`), et la declaration le nomme. Une ORG qui
 * personnalise une app n'ecrit aucun code : son bouton « Générer maintenant »
 * nommerait un effet que l'app ne fournit pas, et l'ecran refuserait de monter.
 * Un effet `socle:` est donc fourni par le runtime, jamais par l'app, et la
 * LISTE est fermee (`EFFETS_DU_SOCLE`, `@taki/data-model`) : le schema refuse
 * un nom inconnu, et la table ci-dessous doit les couvrir tous, le compilateur
 * y veille (`Record<EffetDuSocle, ...>`).
 *
 * CE QU'UN EFFET DU SOCLE A DE PLUS qu'un effet d'app : il RECHARGE l'ecran
 * quand il a ecrit, et il ANNONCE son resultat. Un bouton qui lance un travail
 * serveur sans rien dire laisse la personne recliquer, et le travail partir
 * deux fois.
 */

/** Ce qu'un effet recoit au clic : le contexte de l'ecran, plus le SDK et l'org. */
export interface ContexteDAppDeclaree extends ContexteDAction {
  sdk: TakiSdk;
  /** L'org courante, `''` quand l'hote ne la fournit pas. */
  orgId: string;
}

/** Un effet d'app : il peut ignorer son argument, un `() => void` reste valide. */
export type EffetDAppDeclaree = (contexte: ContexteDAppDeclaree) => void;

/** Ce qu'un effet du socle dit a l'ecran pendant et apres son travail. */
export interface AvisDEffet {
  etat: 'enCours' | 'fait' | 'echec';
  /** Le mot court, rendu sur le bouton lui-meme. */
  texte: string;
  /** La phrase entiere, rendue au survol quand le mot court ne suffit pas. */
  detail?: string;
}

/** Ce que le runtime ajoute au contexte d'un effet du socle. */
export interface ContexteDEffetSocle extends ContexteDAppDeclaree {
  /** Le jeu de la porte qui porte le bouton : sa famille et son type. Absent dans un test sans ecran. */
  jeu?: { family: string; type?: string };
  /** Relit les jeux de l'ecran (compteurs et carte liee compris). */
  recharger(): void;
  annoncer(avis: AvisDEffet): void;
}

type EffetSocle = (contexte: ContexteDEffetSocle) => Promise<void>;

/** Ce nom designe-t-il un effet du socle (connu ou non) ? */
export function estEffetSocle(nom: string): boolean {
  return nom.startsWith(PREFIXE_EFFET_SOCLE);
}

/** Ce nom est-il un effet du socle que le runtime sait faire ? */
export function effetSocleConnu(nom: string): nom is EffetDuSocle {
  return (EFFETS_DU_SOCLE as readonly string[]).includes(nom);
}

/** Un nombre et son nom, accordes : « 1 fiche créée », « 3 fiches créées ». */
function accorde(n: number, un: string, plusieurs: string): string {
  return `${n} ${n > 1 ? plusieurs : un}`;
}

/**
 * LE RESULTAT D'UN PASSAGE, EN MOTS. Fonction PURE et exportee : c'est la seule
 * chose que la personne lit apres le clic, un test la tient.
 *
 * UN ECHEC PARTIEL L'EMPORTE sur le reste : un plan en erreur ne doit pas se
 * cacher derriere « 2 fiches créées ». Le detail nomme ses messages, le journal
 * du plan les garde.
 */
export function avisDuLancement(resultat: ResultatLancement): AvisDEffet {
  const cree =
    resultat.crees > 0 ? accorde(resultat.crees, 'fiche créée', 'fiches créées') : 'Rien à créer';
  if (resultat.echecs.length > 0) {
    return {
      etat: 'echec',
      texte: accorde(resultat.echecs.length, 'plan en échec', 'plans en échec'),
      detail: `${cree}. ${resultat.echecs.map((echec) => echec.message).join(' ; ')}`,
    };
  }
  return {
    etat: 'fait',
    texte: cree,
    detail:
      resultat.plans === 0
        ? 'Aucun plan actif.'
        : `${accorde(resultat.plans, 'plan lu', 'plans lus')}, ${cree.toLowerCase()}.`,
  };
}

/**
 * `socle:lancerRecurrences` : un passage IMMEDIAT des plans, sans attendre
 * celui de la nuit. Depuis la fiche d'un plan, ce plan seul ; ailleurs, tous
 * les plans actifs de l'org (le serveur cadre, `lancerRecurrences`).
 */
const lancerRecurrences: EffetSocle = async (contexte) => {
  contexte.annoncer({ etat: 'enCours', texte: 'Passage en cours...' });
  let resultat: ResultatLancement;
  try {
    resultat = await contexte.sdk.recurrences.lancer(contexte.ficheId ?? undefined);
  } catch (cause) {
    /* LE SDK TRADUIT DEJA l'echec de la callable en phrase francaise
       (`RecurrencesError`) : une Function pas deployee, un acces en lecture. */
    contexte.annoncer({
      etat: 'echec',
      texte: 'Échec du passage',
      detail: cause instanceof Error && cause.message !== '' ? cause.message : "Rien n'a été créé.",
    });
    return;
  }
  contexte.annoncer(avisDuLancement(resultat));
  /* LA RELECTURE SUIT LE RESULTAT, meme sans fiche creee : le journal et la
     prochaine date du plan ont bouge cote serveur. */
  contexte.recharger();
};

/**
 * « NOTIFIER » : le bouton d'une porte qui declenche les regles `action` de
 * l'org visant cette famille (et ce type), sur la fiche ouverte. Les regles
 * disent quoi envoyer et a qui (`docs/NOTIFICATIONS.md`) ; le bouton ne porte
 * aucun texte, donc un owner change le message sans toucher a l'app. Sans
 * fiche ouverte, ou sans regle `action` pour cette porte, l'avis le dit.
 */
const notifier: EffetSocle = async (contexte) => {
  if (contexte.ficheId == null) {
    contexte.annoncer({ etat: 'echec', texte: 'Aucune fiche ouverte', detail: 'Ouvrez une fiche, puis cliquez à nouveau.' });
    return;
  }
  contexte.annoncer({ etat: 'enCours', texte: 'Envoi...' });
  let regles: Array<{ id: string; nom: string }>;
  try {
    const toutes = await contexte.sdk.notifications.regles.list();
    regles = toutes.filter(
      (r) =>
        r.actif &&
        r.declencheur === 'action' &&
        (contexte.jeu == null || (r.famille === contexte.jeu.family && (r.type == null || contexte.jeu.type == null || r.type === contexte.jeu.type))),
    );
  } catch (cause) {
    contexte.annoncer({ etat: 'echec', texte: 'Règles illisibles', detail: cause instanceof Error ? cause.message : String(cause) });
    return;
  }
  if (regles.length === 0) {
    contexte.annoncer({ etat: 'echec', texte: 'Aucune règle', detail: "Aucune règle « à la demande » active ne vise cette porte : Paramètres > Notifications." });
    return;
  }
  let crees = 0;
  let existants = 0;
  const ecartes: string[] = [];
  for (const regle of regles) {
    try {
      const bilan = await contexte.sdk.notifications.testerRegle(regle.id, contexte.ficheId);
      crees += bilan.crees;
      existants += bilan.existants;
      ecartes.push(...bilan.ecartes.map((e) => `${e.destinataire} (${e.raison})`));
    } catch (cause) {
      contexte.annoncer({ etat: 'echec', texte: `Échec : ${regle.nom}`, detail: cause instanceof Error ? cause.message : String(cause) });
      return;
    }
  }
  const texte = crees > 0 ? accorde(crees, 'message envoyé', 'messages envoyés') : existants > 0 ? 'Déjà envoyé' : 'Aucun destinataire';
  contexte.annoncer({
    etat: crees > 0 || existants > 0 ? 'fait' : 'echec',
    texte,
    detail: [
      `${accorde(regles.length, 'règle jouée', 'règles jouées')}.`,
      existants > 0 ? `${accorde(existants, 'message déjà en file', 'messages déjà en file')}.` : '',
      ecartes.length > 0 ? `Écartés : ${ecartes.join(' ; ')}.` : '',
    ].filter((l) => l !== '').join(' '),
  });
};

/**
 * LES EFFETS DE FICHE N'ONT PAS DE BOUTON ICI : ils vivent dans le menu « ... »
 * de la fiche ouverte (`gestes-de-fiche.ts`), jamais dans la rangee 3a.
 */
const EFFETS_SOCLE: Readonly<Record<Exclude<EffetDuSocle, EffetDeFiche>, EffetSocle>> = {
  'socle:lancerRecurrences': lancerRecurrences,
  'socle:notifier': notifier,
};

/**
 * L'EFFET DU SOCLE DE CE NOM, pret a brancher sur une action. LEVE sur un nom
 * inconnu : le schema l'a deja refuse, et un bouton muet serait un bouton qui
 * ment.
 */
export function effetDuSocle(nom: string): EffetSocle {
  if (!effetSocleConnu(nom) || estEffetDeFiche(nom)) {
    throw new Error(
      `L'effet "${nom}" n'est pas un effet du socle (connus : ${EFFETS_DU_SOCLE.join(', ')}).`,
    );
  }
  return EFFETS_SOCLE[nom];
}
